import { ChevronRight, Wrench } from 'lucide-react';
import { Badge } from '@staff-app/ui';
import { formatDateTimeCompact } from '@/lib/format-date';
import { statusVariant } from './SessionsTable';
import type { TroubleshootSession } from '../hooks/useTroubleshoot';
import { StaffSessionBadge } from './StaffSessionBadge';

/**
 * Compact scio3-style session card (mirrors the customer DeviceSessionCard). Used where the device is
 * fixed — the per-device drawer — so it leads with the session title/symptom instead of repeating the
 * device on every row. The whole card opens the read-only transcript.
 */
export function SessionCard({
  session,
  onClick,
}: {
  session: TroubleshootSession;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      data-testid="troubleshoot-session-card"
      className="cursor-pointer rounded-lg border bg-card p-3 transition-colors hover:border-primary/40"
    >
      {/* Row 1: title/symptom + "Staff" (staff-started only) + status. */}
      <div className="flex min-w-0 items-center gap-2">
        <Wrench className="h-4 w-4 shrink-0 text-primary" />
        <span className="min-w-0 flex-1 line-clamp-1 text-sm font-medium text-foreground">
          {session.title || 'Troubleshooting session'}
        </span>
        <StaffSessionBadge mode={session.mode} />
        <Badge variant={statusVariant(session.status)} className="shrink-0 text-[10px]">
          {session.status}
        </Badge>
      </div>
      {/* Row 2: time + who started it, with the open affordance. */}
      <div className="mt-1 flex min-w-0 items-center justify-between gap-2">
        <span className="min-w-0 line-clamp-1 text-xs text-muted-foreground">
          {[session.startedAt ? formatDateTimeCompact(session.startedAt) : null, session.actor]
            .filter(Boolean)
            .join(' · ')}
        </span>
        <span className="flex shrink-0 items-center text-xs font-medium text-primary">
          {'View'}
          <ChevronRight className="ml-0.5 h-3.5 w-3.5" />
        </span>
      </div>
    </div>
  );
}
